import { escapeHtml } from './html';
import { matchCookie, TRACKERS, type Duration, type TrackerId } from './trackers';
import { textsFor } from './translations';
import { isBuiltInLanguage } from './translations';
import type { CategoryName } from './types';

/**
 * Cookie tables, built automatically from what Kockica knows about the page:
 * the consent cookie, the trackers it detected or was told about, and first-party cookies it can see now.
 *
 * Shortcode for the cookie / privacy policy page:  <div data-kockica-cookie-table></div>
 */

export const TABLE_ATTRIBUTE = 'data-kockica-cookie-table';

export interface CookieRow {
  name: string;
  provider: string;
  purpose: string;
  duration: string;
}

export type CookieRows = Partial<Record<CategoryName | 'other', CookieRow[]>>;

export interface TableInput {
  language: string;
  trackers: readonly TrackerId[];
  consentCookie: { name: string; days: number };
  /** Who sets first-party cookies of the site itself (the controller). */
  siteProvider: string;
  /** Cookie names currently visible to the page (document.cookie). */
  visibleCookies: readonly string[];
}

export function formatDuration(duration: Duration, language: string, sessionLabel: string): string {
  if (!duration) return sessionLabel;
  try {
    return new Intl.NumberFormat(language, { style: 'unit', unit: duration.unit, unitDisplay: 'long' }).format(duration.value);
  } catch {
    return `${duration.value} ${duration.unit}${duration.value === 1 ? '' : 's'}`;
  }
}

/** Build the rows per category. Pure apart from Intl; unit-tested directly. */
export function buildCookieRows(input: TableInput): CookieRows {
  const lang = input.language;
  const texts = textsFor(lang);
  const purposeLang = isBuiltInLanguage(lang) ? lang : 'en';
  const rows: CookieRows = {};
  const add = (category: CategoryName | 'other', row: CookieRow): void => {
    const list = (rows[category] ??= []);
    if (!list.some((r) => r.name === row.name && r.provider === row.provider)) list.push(row);
  };

  add('necessary', {
    name: input.consentCookie.name,
    provider: input.siteProvider,
    purpose: texts.consentCookiePurpose,
    duration: formatDuration({ value: input.consentCookie.days, unit: 'day' }, lang, texts.session),
  });

  const seen = new Set<TrackerId>(input.trackers);
  // Cookies already in the browser reveal trackers too (e.g. set on an earlier page).
  for (const name of input.visibleCookies) {
    const hit = matchCookie(name);
    if (hit) seen.add(hit.tracker.id);
  }

  // Database order, so tables read the same on every page and visit.
  for (const tracker of TRACKERS.filter((t) => seen.has(t.id))) {
    for (const cookie of tracker.cookies) {
      add(tracker.category, {
        name: cookie.display,
        provider: cookie.firstParty ? tracker.provider : `${tracker.provider} (${tracker.label})`,
        purpose: tracker.purpose[purposeLang],
        duration: formatDuration(cookie.duration, lang, texts.session),
      });
    }
  }

  for (const name of input.visibleCookies) {
    if (name === input.consentCookie.name || matchCookie(name)) continue;
    add('other', { name, provider: input.siteProvider, purpose: '—', duration: '—' });
  }
  return rows;
}

const ORDER: Array<CategoryName | 'other'> = ['necessary', 'functional', 'analytics', 'marketing', 'other'];

function categoryTitle(category: CategoryName | 'other', language: string): string {
  const texts = textsFor(language);
  if (category === 'necessary') return texts.necessaryTitle;
  if (category === 'other') return texts.otherCookiesTitle;
  return texts.categories[category].title;
}

export function renderTableHtml(rows: CookieRows, language: string): string {
  const t = textsFor(language);
  const parts: string[] = [];
  for (const category of ORDER) {
    const list = rows[category];
    if (!list?.length) continue;
    const head = [t.tableName, t.tableProvider, t.tablePurpose, t.tableDuration].map((h) => `<th scope="col">${escapeHtml(h)}</th>`).join('');
    const body = list
      .map(
        (r) =>
          `<tr><td><code>${escapeHtml(r.name)}</code></td><td>${escapeHtml(r.provider)}</td><td>${escapeHtml(r.purpose)}</td><td>${escapeHtml(r.duration)}</td></tr>`,
      )
      .join('');
    const note = category === 'other' ? `<p>${escapeHtml(t.otherCookiesNote)}</p>` : '';
    parts.push(
      `<section class="kockica-cookie-table__group" data-category="${category}"><h3>${escapeHtml(categoryTitle(category, language))}</h3>${note}` +
        // Own scroll box: a wide table must never make the whole page wider than the screen (it pushes the banner off it).
        `<div class="kockica-cookie-table__scroll"><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div></section>`,
    );
  }
  const onlyConsentCookie = Object.keys(rows).length === 1;
  if (onlyConsentCookie) parts.push(`<p>${escapeHtml(t.noCookies)}</p>`);
  return parts.join('');
}

/** Fill every shortcode element on the page. Safe to call again (e.g. after a language change). */
export function renderCookieTables(rows: CookieRows, language: string, root: ParentNode = document): number {
  const targets = root.querySelectorAll<HTMLElement>(`[${TABLE_ATTRIBUTE}]`);
  const html = renderTableHtml(rows, language);
  targets.forEach((el) => {
    el.classList.add('kockica-cookie-table');
    el.innerHTML = html;
  });
  return targets.length;
}

export function visibleCookieNames(): string[] {
  if (typeof document === 'undefined' || !document.cookie) return [];
  return document.cookie
    .split(';')
    .map((c) => c.split('=')[0]?.trim() ?? '')
    .filter(Boolean);
}
